import { unpackToDate, unpackToDateTime } from '@campfire/hot-date';
import { Box, Typography, Theme } from '@material-ui/core';
import { Warning } from '@material-ui/icons';
import { Link } from 'react-router-dom';
import React, { ReactNode } from 'react';
import { createStyles, makeStyles } from '@material-ui/styles';
import classNames from 'classnames';
import { useCampfireTheme } from '../../../theme/useCampfireTheme';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    card: {
      display: 'flex',
      width: '100%',
      borderRadius: '8px',
      boxSizing: 'border-box',
      overflow: 'hidden',
      '&:hover': {
        cursor: 'pointer',
        boxShadow: `0px 0px 4px rgba(0, 0, 0, 0.25)`,
      },
    },
    cardNoHover: {
      display: 'flex',
      width: '100%',
      borderRadius: '8px',
      boxSizing: 'border-box',
      overflow: 'hidden',
    },
    conflictIndicator: {
      backgroundColor: theme.alert.yellow.extraLight,
      color: theme.alert.yellow.dark,
      padding: '10px 12px 16px 12px',
      fontSize: '12px',
      borderRadius: '8px 8px 0 0',
      marginBottom: '-10px',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '8px',
      animation: '$slideDown 0.3s ease-out',
      border: `1px solid ${theme.alert.yellow.light}`,
      borderBottom: 'none',
    },
    '@keyframes slideDown': {
      '0%': {
        transform: 'translateY(-100%)',
        opacity: 0,
      },
      '100%': {
        transform: 'translateY(0)',
        opacity: 1,
      },
    },
  })
);

interface LinkInfo {
  pathname: string;
  search: string;
}

interface DateInfoCardProps {
  date: string;
  color?: {
    primary: string;
    secondary: string;
    additional?: string;
  };
  children: ReactNode;
  size?: 's' | 'm' | 'l';
  selected?: boolean;
  noHover?: boolean;
  showYear?: boolean;
  conflict?: boolean;
  classes?: { [key: string]: string };
}
interface Props extends DateInfoCardProps {
  linkInfo?: LinkInfo;
}

const Card = (props: DateInfoCardProps) => {
  const { date, color, children, selected, size, noHover, showYear, conflict, classes: customClasses = {} } = props;
  const { theme, isMobile, isMd } = useCampfireTheme();
  const classes = useStyles();

  // Get org name from localStorage or default
  const orgName = localStorage.getItem('orgName') || 'your organisation';

  const year = showYear
    ? unpackToDateTime(date)
        .year.toString()
        .substring(2, 4)
    : '';
  return (
    <Box style={{ width: '100%' }}>
      {conflict && (
        <Box className={classes.conflictIndicator}>
          <Warning style={{ fontSize: '14px', color: theme.alert.yellow.light, marginTop: '2px' }} />
          <Box style={{ lineHeight: 1.2, textAlign: 'left', flex: 1 }}>
            <Typography style={{ fontSize: '12px', fontWeight: 700, lineHeight: 'inherit' }}>
              Rostered during Unavailability
            </Typography>
            <Typography style={{ fontSize: '12px', fontWeight: 400, lineHeight: 'inherit' }}>
              Unavailable? Contact your manager or {orgName} directly to alert them
            </Typography>
          </Box>
        </Box>
      )}
      <Box
        className={classNames(customClasses.root, { [classes.cardNoHover]: noHover, [classes.card]: !noHover })}
        border={selected ? `2px solid ${color?.primary}` : `1px solid ${theme.color.grey.border}`}
        borderBottom={selected ? `2px solid ${color?.primary}` : `1px solid ${theme.color.grey.border}`}
        borderRight={selected ? `2px solid ${color?.primary}` : `1px solid ${theme.color.grey.border}`}
        bgcolor={color?.primary ?? theme.color.grey.neutralBrand100}
        overflow={'hidden'}
      >
        <Box
          display={'flex'}
          flexDirection={'column'}
          alignItems={'center'}
          justifyContent={'center'}
          minWidth={'64px'}
          padding={''}
          width={!isMobile ? (size === 's' ? '64px' : size === 'm' ? '90px' : '120px') : '80px'}
          borderRadius={'8px 0px 0px 8px'}
          className={customClasses.dayContainer}
        >
          <Typography
            variant={'h5'}
            style={{
              color: color && color.additional ? color.additional : color ? 'white' : theme.color.grey.neutral300,
              fontWeight: 900,
              fontSize: '30px',
            }}
            className={customClasses.dayText}
          >
            {unpackToDate(date).day}
          </Typography>
          <Typography
            className={customClasses.monthText}
            variant={'body2'}
            style={{ color: color?.secondary ?? theme.color.grey.neutral200, fontSize: '14px', fontWeight: 'bold' }}
          >
            {unpackToDate(date).monthShort} {year}
          </Typography>
        </Box>
        <Box
          display={'flex'}
          flexDirection={'column'}
          flexGrow={2}
          alignItems={'flex-start'}
          justifyContent={'center'}
          style={{ padding: isMobile ? '12px 12px' : '18px 18px' }}
          overflow={'hidden'}
          width={!isMobile || !isMd ? '300px' : '250px'}
          bgcolor={theme.color.white.neutral50}
          className={customClasses.bodyContainer}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export const DateInfoCard = (props: Props) => {
  const { date, linkInfo, color, children, selected, size, noHover, showYear, conflict, classes } = props;
  if (linkInfo) {
    const { pathname, search } = linkInfo;
    return (
      <Link
        to={{
          pathname,
          search,
        }}
        style={{ textDecoration: 'none', width: '100%', color: '#000' }}
      >
        <Card
          date={date}
          color={color}
          selected={selected}
          size={size}
          noHover={noHover}
          classes={classes}
          showYear={showYear}
          conflict={conflict}
        >
          {children}
        </Card>
      </Link>
    );
  }
  return (
    <Card
      date={date}
      color={color}
      selected={selected}
      size={size}
      noHover={noHover}
      classes={classes}
      showYear={showYear}
      conflict={conflict}
    >
      {children}
    </Card>
  );
};
